import { useEffect, useRef, useState } from 'react'
import { usePrefersReducedMotion } from './useInView'

/**
 * Progress of the viewport through an element, 0 → 1.
 *
 * 0 when the element's top reaches the bottom of the viewport, 1 when its
 * bottom passes the top. Used to draw timeline rails as the reader scrolls.
 * Returns 1 immediately under reduced motion so nothing stays half-drawn.
 */
export function useSectionProgress<T extends HTMLElement>() {
  const ref = useRef<T>(null)
  const reduced = usePrefersReducedMotion()
  const [progress, setProgress] = useState(0)

  useEffect(() => {
    if (reduced) {
      setProgress(1)
      return
    }

    const node = ref.current
    if (!node) return

    let frame = 0

    const update = () => {
      frame = 0
      const rect = node.getBoundingClientRect()
      const viewport = window.innerHeight
      // Bias the window so the rail finishes a little before the section
      // leaves, rather than completing off-screen.
      const start = viewport * 0.85
      const span = rect.height + start - viewport * 0.25
      const travelled = start - rect.top
      setProgress(Math.max(0, Math.min(1, travelled / Math.max(1, span))))
    }

    const onScroll = () => {
      if (frame) return
      frame = requestAnimationFrame(update)
    }

    update()
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onScroll, { passive: true })

    return () => {
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onScroll)
      if (frame) cancelAnimationFrame(frame)
    }
  }, [reduced])

  return { ref, progress }
}

/**
 * Reports which of the given element ids is currently the reading target,
 * so an in-page index can highlight it.
 */
export function useScrollSpy(ids: string[], offset = 140): string | null {
  const [active, setActive] = useState<string | null>(ids[0] ?? null)

  useEffect(() => {
    if (ids.length === 0) return

    let frame = 0

    const update = () => {
      frame = 0
      let current: string | null = null

      for (const id of ids) {
        const el = document.getElementById(id)
        if (!el) continue
        // The last section whose top has passed the offset line wins.
        if (el.getBoundingClientRect().top - offset <= 0) current = id
      }

      // Before the first section, keep the first one highlighted.
      setActive(current ?? ids[0] ?? null)
    }

    const onScroll = () => {
      if (frame) return
      frame = requestAnimationFrame(update)
    }

    update()
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onScroll, { passive: true })

    return () => {
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onScroll)
      if (frame) cancelAnimationFrame(frame)
    }
  }, [ids, offset])

  return active
}
